/* eslint-disable max-len */
import React, { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import moment from 'moment';
import {
  Alert,
  Button,
  Chip,
  Tooltip,
} from '@mui/material';
import { Navigate } from 'react-router-dom';
import ContentBox from '../components/layout/ContentBox';
import TrialSearch from '../components/trial/TrialSearch';
import TrialInvoiceDialog from '../components/trial/TrialInvoiceDialog';
import {
  resetSubmissionState,
  fetchTrialTags,
  showErrorNotification,
  setSelectedTab,
  setLoading,
  resetDigitalSurvey,
} from '../redux/actions';
import { redirectInternalNewTab } from '../utilities/urlHelper';
import Enrollmentslist, { ExtendedTrial, EnrollmentExteded } from '../components/trial/EnrollmentsList';
import ApiClient from '../loopback/apiClient';
import { EnrollmentsFull } from '../loopback/apiClient/EnrollmentApi';
import { convertToQid, getEnrollmentTextTranslation } from '../utilities/stringHelper';
import {
  Enrollment,
  RootState,
  Trial,
  TrialCategoryValue,
  ZerpaState,
} from '../redux/types';
import { ZerpaTableStateSetters } from '../components/layout/table/ZerpaTable';
import TrialTypeTabs from '../components/trial/TrialTypeTabs';
import withRouter, { RouteType } from '../hoc/withRouter';

const additionalInfoLinkMap: Record<string, string> = {
  CD117: 'https://quip.eu/de_DE/2018/05/18/ergaenzungen-zur-anleitung-cd117-anordnung-der-tma-schnitte',
  IDH1: 'https://quip.eu/de_DE/2018/06/19/ergaenzungen-zur-anleitung-idh1-zuordnung-der-objekttraeger',
  H2ISHG: 'https://quip.eu/de_DE/2018/09/24/erganzungen-zur-anleitung-her2-magen-ish-ihc-anordnung-der-tma-schnitte/',
  H2IMG: 'https://quip.eu/de_DE/2018/09/24/erganzungen-zur-anleitung-her2-magen-ish-ihc-anordnung-der-tma-schnitte/',
  'ALK-NGS-19': 'https://www.easyzoom.com/albumaccess/06a326b03f6f4c1780b654e1dc6a1dc2',
  'ALK-ISH-19': 'https://www.easyzoom.com/albumaccess/fdec2da08bcd42ea815c32c196ec8a36',
  'ALK-IHC-19': 'https://www.easyzoom.com/albumaccess/dea0a978b71b432eac3828b63f128f03',
};

type Props = { intl: IntlType } & PropsFromRedux & RouteType;

type State = {
  invoiceEnrollment?: Enrollment;
  query?: string;
  year: string;
  showDeactivatedTrials: boolean;
  enrollments?: EnrollmentsFull[];
  tags: number[];
  selectedTab: 'institute' | 'individual';
};

class EnrollmentsView extends Component<Props, State> {
  static canReviewSubmission = (trial: ExtendedTrial) => {
    if (trial.enrollment.allowPostResubmit) {
      return false;
    }

    const trialEnd = moment.utc(trial.endDate).add(1, 'day');
    return trial
      && trial.enrollment
      && trial.enrollment.submission
      && moment.utc().isAfter(trialEnd);
  };

  zerpaTableSetter: ZerpaTableStateSetters | undefined;

  constructor(props: Props) {
    super(props);
    this.state = {
      invoiceEnrollment: undefined,
      query: undefined,
      year: moment().utc().format('YYYY'),
      showDeactivatedTrials: false,
      enrollments: undefined,
      tags: [],
      selectedTab: props.selectedTab,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0); // this is a smally hack due to way the component is loaded by router
    const { userId, fetchTrialTags: fetchTrialTagsAction, trialTags } = this.props;
    if (!userId) {
      return;
    }

    if (!trialTags) {
      fetchTrialTagsAction();
    }

    this.fetchNewEnrollments();
  }

  onSearch = ({ searchQuery, tags, year }: { searchQuery: string; tags: number[]; year: string }) => {
    const { year: currentStateYear } = this.state;
    this.setState({
      query: searchQuery,
      year: year || currentStateYear,
      tags,
    }, this.fetchNewEnrollments);
  };

  onShowDeactivatedTrialsClicked = (checked: boolean) => {
    this.setState({ showDeactivatedTrials: checked });
  };

  onGotoSurvey = async (trial: ExtendedTrial) => {
    if (!(await this.checkAllowedToPostResubmit(trial))) {
      return;
    }
    if (trial.survey && trial.version !== 2) {
      redirectInternalNewTab(`survey/${trial.id}/${trial.enrollment.id}`);
    }
    const { router } = this.props;
    if (trial.digitalSurveys && trial.isDRT) {
      router.navigate(`../digitalTrial/${trial.id}/menu`);
    } else if (trial.version === 2) {
      router.navigate(`../trial/${trial.id}/survey`);
    }
  };

  static onReviewSubmission = (enrollmentId: number) => {
    redirectInternalNewTab(`showSubmission/${enrollmentId}`);
  };

  checkAllowedToPostResubmit = async (trial: ExtendedTrial): Promise<boolean> => {
    const { showErrorNotification: showErrorNotificationAction } = this.props;
    const { id } = trial.enrollment.submission || {};
    const { allowPostResubmit } = trial.enrollment;

    if (!id || !allowPostResubmit) {
      // for first time, and allowPostResubmit is false, allow to go through
      return true;
    }

    const res = await ApiClient.checkExpiredAndReset(id);
    if (res) {
      showErrorNotificationAction(<FormattedMessage id="enrollmentsmanager.postResubmitExpired" />);
      return false;
    }
    return true;
  };

  onZerpaTableStateChange = (_zerpaState: ZerpaState, stateSetters?: ZerpaTableStateSetters) => {
    if (!this.zerpaTableSetter) {
      this.zerpaTableSetter = stateSetters;
    }
  };

  onTypeTabChange = (tabId: 'institute' | 'individual') => {
    const { setSelectedTab: setSelectedTabAction } = this.props;

    setSelectedTabAction('enrollmentmanagement', tabId);
    this.setState({ selectedTab: tabId }, this.fetchNewEnrollments);
  };

  isSurveyAvailable = (trial: ExtendedTrial, enrollment: ExtendedTrial['enrollment']) => {
    const { intl } = this.props;
    const {
      isDRT, version, survey, startDate, endDate,
    } = trial;
    const oldSurvey = !version || version === 1;
    if (oldSurvey && !survey && !isDRT) {
      return {
        show: false,
        tooltip: intl.formatMessage({ id: 'enrollmentview.trialhasnotstarted' }),
      };
    }

    if (enrollment?.allowPostResubmit || isDRT) {
      return { show: true };
    }

    const trialStart = moment.utc(startDate);
    const trialEnd = moment.utc(endDate).add(1, 'day'); // must add as we always use start of the end-day
    const trialIsReady = moment.utc().isBetween(trialStart, trialEnd);
    if (!trialIsReady) {
      return {
        show: false,
        tooltip: intl.formatMessage({ id: 'enrollmentview.trialhasnotstarted' }),
      };
    }

    const isApproved = enrollment.status === 'approved';
    if (!isApproved) {
      return {
        show: false,
        tooltip: intl.formatMessage({ id: 'enrollmentview.pendingapproval' }),
      };
    }

    return { show: true };
  };

  createCurrentTrialsList = (): EnrollmentExteded[] => {
    // we use the country from redux as the user will always have the
    // same institute
    const { instituteCountry, trialTags } = this.props;
    const { enrollments } = this.state;
    if (!enrollments || !enrollments.values) {
      return [];
    }
    return enrollments.map((enrollment) => {
      const {
        title, firstname, surname, id, userCountry,
      } = enrollment.quipUser;
      const enroller = `${title} ${firstname} ${surname} (${convertToQid(id, instituteCountry || userCountry)})`;
      const { trial } = enrollment;
      const { trialTagMapping } = trial;
      const statusText = getEnrollmentTextTranslation(enrollment.status);
      return {
        id: enrollment.id,
        status: enrollment.status,
        statusText: <FormattedMessage id={statusText} />,
        trialName: trial.name,
        trialCode: trial.code,
        trialManual: trial.manual,
        tag: trialTagMapping?.map((tag) => (
          <Tooltip key={tag.tagId} title={trialTags?.[tag.tagId].description || ''}>
            <Chip size="small" label={trialTags?.[tag.tagId].name || ''} key={tag.id} style={{ margin: '0.1em' }} />
          </Tooltip>
        )),
        trialDeactivated: trial.deactivate,
        endYear: moment(trial.endDate).year(),
        enrollment,
        enroller,
        trial,
      } as EnrollmentExteded;
    });
  };

  static renderAdditionalInformationLink = (trial: Trial) => {
    if (additionalInfoLinkMap[trial.code]) {
      return (
        <span className="ml3">
          <span>
            Achtung: Ergänzungen zur Anleitung
          </span>
          <a
            href={additionalInfoLinkMap[trial.code]}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              textDecoration: 'underline',
              marginLeft: 5,
            }}
          >
            hier
          </a>
        </span>
      );
    }

    return null;
  };

  renderTrialAction = (trial: ExtendedTrial) => {
    const {
      trialCategories,
      resetSubmissionState: dispatchResetSubmissionState,
      resetDigitalSurvey: dispatchResetDigitalSurvey,
      router,
    } = this.props;
    const { invoiceEnrollment } = this.state;
    const trialCategoryIdMap = trialCategories.reduce((acc, category) => {
      acc[category.id] = category.categoryId;
      return acc;
    }, {} as Record<number, TrialCategoryValue>);

    if (trial.enrollment.status === 'applied') {
      return (
        <Alert severity="error">
          <FormattedMessage id="payment.enrollmentPending" />
        </Alert>
      );
    }

    const showSurveyButton = this.isSurveyAvailable(trial, trial.enrollment);
    return (
      <div>
        {
          EnrollmentsView.canReviewSubmission(trial)
          && (
            <Button
              variant="contained"
              id={`ReviewSubmission-${trial.code}`}
              className="mb2 mr2"
              onClick={() => EnrollmentsView.onReviewSubmission(trial.enrollment.id)}
              disabled={!EnrollmentsView.canReviewSubmission(trial)}
              color="primary"
            >
              <FormattedMessage id="enrollmentsmanager.reviewSubmission" />
            </Button>
          )
        }
        <Tooltip title={!showSurveyButton.show && showSurveyButton.tooltip ? showSurveyButton.tooltip : ''}>
          <span>
            <Button
              variant="contained"
              id={`EnrolledTrial-${trial.code}`}
              className="mb2 mr2"
              onClick={() => {
                dispatchResetSubmissionState();
                dispatchResetDigitalSurvey();
                this.onGotoSurvey(trial);
              }}
              disabled={!showSurveyButton.show}
              color="primary"
            >
              <FormattedMessage id="enrollmentsmanager.goToSurvey" />
            </Button>
          </span>
        </Tooltip>
        {
          trial.trialCategory
          && trialCategoryIdMap[trial.trialCategory] !== TrialCategoryValue.voucherTrial
          && trialCategoryIdMap[trial.trialCategory] !== TrialCategoryValue.internalTrial
          && trial.priceInEUR !== 0
          && (
            <Button
              variant="contained"
              id="EnrolledTrial-invoiceButton"
              className="mb2 mr2"
              onClick={() => this.setState({ invoiceEnrollment: trial.enrollment })}
              disabled={
                (trial.enrollment.status !== 'approved' && trial.enrollment.status !== 'cancelled')
              }
              color="primary"
            >
              <FormattedMessage id="enrollmentsmanager.invoiceDialogOpenButton" />
            </Button>
          )
        }
        <TrialInvoiceDialog
          enrollment={invoiceEnrollment}
          trial={trial}
          onClose={() => {
            this.setState({ invoiceEnrollment: undefined });
          }}
          isOpen={invoiceEnrollment !== undefined}
          navigate={router.navigate}
        />
        {
          EnrollmentsView.renderAdditionalInformationLink(trial)
        }
      </div>
    );
  };

  static renderTrialStatus = (trial: ExtendedTrial) => {
    const now = moment.utc();
    const trialStart = moment.utc(trial.startDate);
    const trialEnd = moment.utc(trial.endDate);
    if (now.isBefore(trialStart)) {
      return <FormattedMessage id="trialtable.waitingForStart" />;
    }
    if (now.isBetween(trialStart, trialEnd)) {
      return <FormattedMessage id="trialtable.ongoingStatus" />;
    }
    if (now.isAfter(trialEnd)) {
      if (trial.enrollment
        && trial.enrollment.certificate
        && trial.enrollment.certificate.approved) {
        return <FormattedMessage id="trialtable.finished" />;
      }
      return <FormattedMessage id="trialtable.pendingStatus" />;
    }
    return <FormattedMessage id="trialtable.unknownStatus" />;
  };

  fetchNewEnrollments() {
    const { instituteId, userId, setLoading: dispatchSetLoading } = this.props;
    const {
      year,
      query,
      tags,
      selectedTab,
    } = this.state;

    const processNewEnrollments = (result: EnrollmentsFull[]) => {
      if (tags && tags.length > 0) {
        const filteredTags = (result)?.filter((enrollment) => {
          const { trialTagMapping } = enrollment.trial;
          if (!trialTagMapping || trialTagMapping.length === 0) {
            return false;
          }
          const tagIds = trialTagMapping.map((tag) => tag.tagId);
          return tags.find((tag) => tagIds.includes(tag));
        });
        this.setState({ enrollments: filteredTags });
      } else {
        this.setState({ enrollments: result });
      }
    };

    dispatchSetLoading(true);
    if (selectedTab === 'institute' && instituteId) {
      ApiClient.getEnrollmentsByInstitute(instituteId, Number(year), query).then(processNewEnrollments)
        .finally(() => dispatchSetLoading(false));
    } else if (userId) {
      ApiClient.getEnrollmentsByIndividual(userId, Number(year), query).then(processNewEnrollments)
        .finally(() => dispatchSetLoading(false));
    }
    if (this.zerpaTableSetter?.setPage) {
      this.zerpaTableSetter?.setPage(0);
    }
  }

  render() {
    const {
      userId,
      trialTags,
    } = this.props;
    const { selectedTab } = this.state;
    if (!userId) {
      return <Navigate to="/login" />;
    }

    const { showDeactivatedTrials } = this.state;

    return (
      <div>
        <ContentBox
          elevation={0}
          title={<FormattedMessage id="submissions.headline" />}
        >
          <TrialTypeTabs
            onTabChange={this.onTypeTabChange}
            selectedTab={selectedTab}
          />
          <TrialSearch
            data={this.state}
            onSearch={this.onSearch}
            onShowDeactivatedTrials={this.onShowDeactivatedTrialsClicked}
            onShowDeactivatedTrialsLabel="trials.showCompletedTrials"
            trialTags={trialTags}
          />
          <Enrollmentslist
            enrollments={this.createCurrentTrialsList()}
            enrollmentType={selectedTab}
            showDeactivatedTrials={showDeactivatedTrials}
            renderTrialActions={this.renderTrialAction}
            onZerpaTableStateChange={this.onZerpaTableStateChange}
          />
        </ContentBox>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  userId: state.users.token && state.users.token.userId,
  instituteId: state.users.token?.institute?.id,
  instituteCountry: state.users.token?.institute?.country,
  trialCategories: state.trialCategories.categories,
  trialTags: state.trialTag.byId,
  selectedTab: state.pageState.enrollmentmanagement.selectedTab,
});

type IntlType = {
  formatMessage: (arg: Record<string, unknown>) => string;
};

const dispatchProps = {
  resetSubmissionState,
  resetDigitalSurvey,
  fetchTrialTags,
  showErrorNotification,
  setSelectedTab,
  setLoading,
};

const connector = connect(mapStateToProps, dispatchProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default injectIntl(connector(withRouter(EnrollmentsView)));
